// Offline PIN unlock (Guide §8.5): a PBKDF2 verifier of the PIN is kept per signed-in principal on this device. It only unlocks local
// data; every server call still needs a real session. Iterations follow OFFLINE_SESSION_PBKDF2_MIN_ITER.
import { OFFLINE_SESSION_PBKDF2_MIN_ITER, normalizePin } from '@dd/shared';
import { kvGet, kvSet } from '@/db';
import type { ProfileDto } from './api';

export interface KnownSession {
  profile: ProfileDto;
  deviceCode: string | null;
  shopName?: string;
  category?: string;
  salt: string;
  hash: string;
  iterations: number;
  lastLoginAt: number;
}

const b64 = (u: Uint8Array): string => btoa(String.fromCharCode(...u));
const unb64 = (s: string): Uint8Array => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations }, key, 256);
  return b64(new Uint8Array(bits));
}

export async function rememberSession(profile: ProfileDto, deviceCode: string | null, pin: string | null, extra: { shopName?: string; category?: string } = {}): Promise<void> {
  const list = (await kvGet<KnownSession[]>('sessions')) ?? [];
  const prev = list.find((s) => s.profile.principalId === profile.principalId && s.profile.shopId === profile.shopId);
  // The PIN belongs to the person, not to one shop: a record created without a PIN (new shop, shop switch) inherits the verifier
  // of the same principal's other record — otherwise the offline unlock after "create shop" would reject the right PIN.
  const donor = prev?.hash ? prev : list.find((s) => s.profile.principalId === profile.principalId && s.hash);
  let { salt, hash, iterations } = donor ?? { salt: '', hash: '', iterations: OFFLINE_SESSION_PBKDF2_MIN_ITER };
  if (pin) {
    const s = crypto.getRandomValues(new Uint8Array(16));
    salt = b64(s);
    iterations = OFFLINE_SESSION_PBKDF2_MIN_ITER;
    hash = await derive(normalizePin(pin), s, iterations);
  }
  const rec: KnownSession = { profile, deviceCode, salt, hash, iterations, lastLoginAt: Date.now(), shopName: extra.shopName ?? prev?.shopName, category: extra.category ?? prev?.category };
  const next = [rec, ...list.filter((s) => !(s.profile.principalId === profile.principalId && s.profile.shopId === profile.shopId))].slice(0, 12);
  await kvSet('sessions', next);
  await kvSet('lastSession', { principalId: profile.principalId, shopId: profile.shopId });
}

export async function lastKnownSession(): Promise<KnownSession | null> {
  const last = await kvGet<{ principalId: string; shopId: string | null }>('lastSession');
  if (!last) return null;
  const list = (await kvGet<KnownSession[]>('sessions')) ?? [];
  return list.find((s) => s.profile.principalId === last.principalId && s.profile.shopId === last.shopId) ?? null;
}
export async function knownSessions(): Promise<KnownSession[]> {
  return (await kvGet<KnownSession[]>('sessions')) ?? [];
}

export async function verifyOffline(rec: KnownSession, pin: string): Promise<boolean> {
  if (!rec.hash) return false;
  return (await derive(normalizePin(pin), unb64(rec.salt), rec.iterations)) === rec.hash;
}

export async function forgetSession(principalId: string, shopId: string | null): Promise<void> {
  const list = (await kvGet<KnownSession[]>('sessions')) ?? [];
  await kvSet('sessions', list.filter((s) => !(s.profile.principalId === principalId && s.profile.shopId === shopId)));
  const last = await kvGet<{ principalId: string; shopId: string | null }>('lastSession');
  if (last && last.principalId === principalId) await kvSet('lastSession', null);
}
